import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';

import HighlightMark from '../../../MyComponents/LandingSections/HighlightMark';
import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../../MyComponents/LandingSections/SectionEyebrow';
import SectionHeading from '../../../MyComponents/LandingSections/SectionHeading';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

// The muted "how it is now" treatment. Local to this band: it is the one place
// the design greys copy out to contrast it with the "with VBoard" list.
const NOW = {
    kicker: '#75675A',
    text: '#6F6155',
    kickerOnDark: '#8FB2B3',
    textOnDark: '#A9C4C4',
    dividerOnDark: 'rgba(255, 255, 255, 0.15)',
};

const useStyles = makeStyles(() => ({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: '26px',
    },
    card: {
        boxSizing: 'border-box',
        position: 'relative',
        // Clips the disc that bleeds off the dark card's corner.
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: '38px',
        borderRadius: '28px',
    },
    light: {
        border: `1px solid ${REDESIGN_COLORS.ruleSand}`,
        background: REDESIGN_COLORS.white,
    },
    dark: {
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.dark,
    },
    eyebrow: {
        alignSelf: 'flex-start',
        marginBottom: '22px',
        padding: '6px 11px',
        background: REDESIGN_COLORS.mist,
        fontSize: '0.72rem',
        letterSpacing: '0.09em',
    },
    eyebrowOnDark: {
        color: REDESIGN_COLORS.mist,
        background: 'rgba(255, 255, 255, 0.1)',
    },
    now: {
        position: 'relative',
        zIndex: 2,
        marginBottom: '24px',
        paddingBottom: '24px',
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
    },
    nowOnDark: {
        borderBottomColor: NOW.dividerOnDark,
    },
    kicker: {
        display: 'block',
        marginBottom: '9px',
        fontSize: '0.7rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
    },
    nowKicker: {
        color: NOW.kicker,
    },
    nowKickerOnDark: {
        color: NOW.kickerOnDark,
    },
    nowText: {
        margin: 0,
        color: NOW.text,
        fontSize: '1.02rem',
    },
    nowTextOnDark: {
        color: NOW.textOnDark,
    },
    withKicker: {
        position: 'relative',
        zIndex: 2,
        marginBottom: '14px',
        color: REDESIGN_COLORS.link,
    },
    withKickerOnDark: {
        color: REDESIGN_COLORS.highlight,
    },
    bullets: {
        position: 'relative',
        zIndex: 2,
        display: 'grid',
        gap: '13px',
        margin: 0,
        padding: 0,
        listStyle: 'none',
        color: REDESIGN_COLORS.dark,
        fontSize: '1.02rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    bulletsOnDark: {
        color: REDESIGN_COLORS.white,
    },
    bullet: {
        display: 'flex',
        gap: '11px',
    },
    tick: {
        color: REDESIGN_COLORS.primary,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    disc: {
        position: 'absolute',
        width: '210px',
        height: '210px',
        right: '-80px',
        bottom: '-90px',
        borderRadius: '50%',
        background: 'rgba(255, 200, 61, 0.15)',
        pointerEvents: 'none',
    },
}));

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * The `#de-ce` band: why VBoard exists, as before/after cards. The schools and
 * tutoring pages render it with their own copy (`keyPrefix`) and `cards`; the
 * landing no longer has one.
 */
const HomeWhy = ({ id = 'de-ce', keyPrefix, cards }) => {
    const classes = useStyles();

    return (
        <LandingSection id={id} paddingTop={40} paddingBottom={48}>
            <SectionHeading
                eyebrow={<FormattedMessage id={`${keyPrefix}.eyebrow`} />}
                title={
                    <FormattedMessage
                        id={`${keyPrefix}.title`}
                        values={{ mark: (chunks) => <HighlightMark>{chunks}</HighlightMark> }}
                    />
                }
                lead={<FormattedMessage id={`${keyPrefix}.lead`} />}
                titleSize="clamp(1.9rem, 3.6vw, 2.9rem)"
                maxWidth={820}
                marginBottom={44}
            />

            <div className={classes.grid}>
                {cards.map(({ key, dark, bullets }) => (
                    <article key={key} className={cx(classes.card, dark ? classes.dark : classes.light)}>
                        <SectionEyebrow variant="card" className={cx(classes.eyebrow, dark && classes.eyebrowOnDark)}>
                            <FormattedMessage id={`${keyPrefix}.${key}.label`} />
                        </SectionEyebrow>

                        <div className={cx(classes.now, dark && classes.nowOnDark)}>
                            <span className={cx(classes.kicker, dark ? classes.nowKickerOnDark : classes.nowKicker)}>
                                <FormattedMessage id={`${keyPrefix}.nowLabel`} />
                            </span>
                            <p className={cx(classes.nowText, dark && classes.nowTextOnDark)}>
                                <FormattedMessage id={`${keyPrefix}.${key}.now`} />
                            </p>
                        </div>

                        <span className={cx(classes.kicker, classes.withKicker, dark && classes.withKickerOnDark)}>
                            <FormattedMessage id={`${keyPrefix}.withLabel`} />
                        </span>
                        <ul className={cx(classes.bullets, dark && classes.bulletsOnDark)}>
                            {Array.from({ length: bullets }, (_, i) => (
                                <li key={i} className={classes.bullet}>
                                    <span aria-hidden="true" className={classes.tick}>
                                        ✓
                                    </span>
                                    <FormattedMessage id={`${keyPrefix}.${key}.bullet${i + 1}`} />
                                </li>
                            ))}
                        </ul>

                        {dark ? <div aria-hidden="true" className={classes.disc} /> : null}
                    </article>
                ))}
            </div>
        </LandingSection>
    );
};

export default HomeWhy;
